import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Check, Download, Sparkles, X } from 'lucide-react';
import { Button, CenteredSpinner, Chip, ErrorState } from '@/components/ui';
import { KIND_ICON, KIND_TINT, cn, formatFileSize } from '@/lib/format';
import { useAsset, useConfirmTags, useTagNow } from '@/lib/queries';

/**
 * Asset detail — preview + metadata + the AI-tag review panel.
 *
 * The tag panel is the parity centrepiece: it shows the five Gemini-generated
 * fields (brand / product title / image type / description / hashtags), whether
 * they're confirmed, and gives Confirm / Reject / Re-tag actions — the human-
 * in-the-loop step the Pimcore `confirmed_id` workflow implemented.
 */
export function AssetDetailPage() {
  const { assetId } = useParams({ from: '/shell/assets/$assetId' });
  const { data: asset, isPending, error, refetch } = useAsset(assetId);
  const tagNow = useTagNow(assetId);
  const confirm = useConfirmTags(assetId);
  // aiTags aren't on the base asset endpoint; fetch them alongside via the tag route result / re-fetch.
  const [localTags, setLocalTags] = useState<Record<string, string> | null>(null);

  if (isPending) return <CenteredSpinner label="Loading asset…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!asset) return null;

  const Icon = KIND_ICON[asset.kind];
  const meta = asset.metadata ?? {};
  const tags = localTags ?? {
    brand: (meta['brand'] as string) ?? '',
    productTitle: (meta['title'] as string) ?? '',
    imageType: (meta['imageType'] as string) ?? '',
    description: (meta['description'] as string) ?? '',
    hashtags: Array.isArray(meta['tags']) ? (meta['tags'] as string[]).map((t) => `#${t}`).join(' ') : '',
  };
  const hasTags = Boolean(tags.brand || tags.productTitle || tags.description || tags.hashtags);

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-[1400px] px-6 py-5">
        <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted hover:text-strong">
          <ArrowLeft className="size-3.5" /> Back
        </Link>

        <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          {/* Preview */}
          <div className="flex items-center justify-center rounded-[var(--radius-card)] surface-sunken border line p-4" style={{ minHeight: 320 }}>
            {asset.kind === 'image' ? (
              <img src={asset.url} alt={asset.fileName} className="max-h-[70vh] max-w-full rounded object-contain" />
            ) : asset.kind === 'video' ? (
              <video src={asset.url} controls className="max-h-[70vh] max-w-full rounded" />
            ) : (
              <div className="flex flex-col items-center gap-3 py-10">
                <Icon className={cn('size-16', KIND_TINT[asset.kind])} />
                <a href={asset.url} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-500 hover:underline">Open file</a>
              </div>
            )}
          </div>

          {/* Details + AI tags */}
          <div className="space-y-5">
            <div>
              <h1 className="break-words text-lg font-semibold text-strong">{asset.fileName}</h1>
              <p className="mt-1 text-xs text-muted">{asset.path}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Chip>{asset.kind}</Chip>
                <Chip>{formatFileSize(asset.sizeBytes)}</Chip>
                {asset.width && asset.height ? <Chip>{asset.width}×{asset.height}</Chip> : null}
              </div>
              <a href={asset.url} download={asset.fileName} className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-500 hover:underline">
                <Download className="size-3.5" /> Download
              </a>
            </div>

            {/* AI tag panel */}
            <div className="rounded-[var(--radius-card)] surface-card border line p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                  <Sparkles className="size-3.5 text-brand-500" /> AI Tags
                </h2>
                <Button
                  variant="secondary"
                  className="h-7 text-xs"
                  loading={tagNow.isPending}
                  onClick={() =>
                    tagNow.mutate(undefined, {
                      onSuccess: (r) => {
                        const t = (r as { tags: Record<string, string> }).tags;
                        setLocalTags(t);
                      },
                    })
                  }
                >
                  {hasTags ? 'Re-tag' : 'Tag now'}
                </Button>
              </div>

              {hasTags ? (
                <dl className="space-y-2 text-sm">
                  <Field label="Brand" value={tags.brand} />
                  <Field label="Product" value={tags.productTitle} />
                  <Field label="Image type" value={tags.imageType} />
                  <Field label="Description" value={tags.description} />
                  {tags.hashtags && (
                    <div>
                      <dt className="text-[11px] text-faint">Hashtags</dt>
                      <dd className="mt-1 flex flex-wrap gap-1">
                        {tags.hashtags.split(/\s+/).filter(Boolean).map((h) => <Chip key={h} tone="brand">{h}</Chip>)}
                      </dd>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button variant="primary" className="h-8 text-xs" loading={confirm.isPending} onClick={() => confirm.mutate(true)}>
                      <Check className="size-3.5" /> Confirm
                    </Button>
                    <Button variant="secondary" className="h-8 text-xs" onClick={() => confirm.mutate(false)}>
                      <X className="size-3.5" /> Backweisen
                    </Button>
                  </div>
                </dl>
              ) : (
                <p className="text-sm text-faint">Not tagged yet. “Tag now” analyses the image with AI.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | undefined }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[11px] text-faint">{label}</dt>
      <dd className="text-body">{value}</dd>
    </div>
  );
}
